import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { useAuth, POST_LOGIN_REDIRECT_KEY } from './AuthContext'
import { PamLoading, PamError } from '../components/PamStateComponents'

/**
 * Resolves where to send the user after a successful login: the path they
 * were actually trying to reach (stashed by login() before the /authorize
 * redirect) when it's a safe same-origin path, otherwise the /admin index.
 *
 * Guards against anything other than a same-origin relative path — sessionStorage
 * is same-origin-only so this isn't a real injection vector, but a malformed or
 * absolute value (e.g. "https://evil.example") must never reach navigate().
 * Exported for unit testing (pure function, no DOM/router dependency).
 */
export function resolvePostLoginRedirect(raw: string | null): string {
  if (!raw) return '/admin'
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/admin'
  return raw
}

// ---------------------------------------------------------------------------
// Themed callback screen (styled-components + Phase-3 PamLoading/PamError —
// replaces the previous MUI Box/CircularProgress/Typography/Button tree).
// See .superpowers/sdd/oidcpam-gate6-shared.md — OIDCPAM Gate-6 Phase 7.
// ---------------------------------------------------------------------------

const Screen = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const ReturnHomeButton = styled.button`
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('md')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: none;
  color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

export const Callback = () => {
  const { handleCallback } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    handleCallback(params)
      .then(() => {
        // Single-use: read then clear, whether or not it's actually restored.
        const stashed = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
        navigate(resolvePostLoginRedirect(stashed), { replace: true })
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in failed')
      })
  }, [])

  if (error) {
    return (
      <Screen>
        <PamError message={`Sign-in error: ${error}`} />
        <ReturnHomeButton
          type="button"
          onClick={() => window.location.assign('/')}
        >
          Return home
        </ReturnHomeButton>
      </Screen>
    )
  }

  return (
    <Screen>
      <PamLoading label="Signing in…" />
    </Screen>
  )
}
